import { showNotification } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';

import { TransactionType } from '~/common/enums/payments.enum';
import { bugSnagLog } from '~/utils/bugsnag';
import { SplitFullNameToFirstLast } from '~/components/utils/formatters';
import { reportEvent } from '~/domains/analytics';
import { usePaymentMethods } from '~/domains/payments/hooks/usePaymentMethods';
import { useWalletBalance } from '~/domains/payments/hooks/useWalletBalance';
import { useWalletRefundableBreakdown } from '~/domains/payments/hooks/useWalletRefundableBreakdown';
import { invalidateDepositBonuses } from '~/domains/payments/deposits/utils/query-invalidation';
import type { PaysafeInstance } from '~/domains/payments/types/paysafe';
import { PaymentType } from '~/services/wallet/interfaces/wallets.interface';
import { depositToWallet } from '~/services/wallet/wallet.service.api';
import { getErrorTranslations } from '~/domains/payments/utils/utils';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';
import { roundToCents, dollarsToCents } from '~/domains/payments/utils/currencyRounding';

import { isCustomDeposit } from './consts';

const enum PaysafeErrorCodes {
  'E5281' = '5281', // https://developer.paysafe.com/en/api-docs/cards/test-and-go-live/card-errors/#common-errors
  'E3007' = '3007', // https://developer.paysafe.com/en/api-docs/cards/test-and-go-live/card-errors/#authorization-errors
  'E9125' = '9125', // https://developer.paysafe.com/en/api-docs/client-side-sdks/paysafejs-powered-by-cards-api/tokenize/#list-of-tokenize-callback-errors
}

type PaysafeError = {
  code: PaysafeErrorCodes;
  detailedMessage: string;
  displayMessage: string;
};

const isPaysafeError = (error: unknown): error is PaysafeError =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  'detailedMessage' in error &&
  'displayMessage' in error;

const DEPOSIT_CONTEXT = 'deposit:new-card';

const toBugSnagError = (error: unknown) => {
  if (isPaysafeError(error)) {
    return {
      errorMessage: `Paysafe card tokenization failed (${error.code})`,
      severity: 'warning' as const,
      errorDetails: {
        code: error.code,
        detailedMessage: error.detailedMessage,
        displayMessage: error.displayMessage,
      },
    };
  }

  if (error instanceof AxiosError) {
    const { response } = error as AxiosError<{ message?: string }>;
    return {
      errorMessage: `Card deposit request failed: ${error.message}`,
      severity: 'error' as const,
      errorDetails: {
        code: error.code,
        status: response?.status,
        responseMessage: response?.data?.message,
      },
    };
  }

  return {
    errorMessage: `Card deposit failed: ${error instanceof Error ? error.message : String(error)}`,
    severity: 'error' as const,
    errorDetails: {
      name: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : undefined,
    },
  };
};

function useDepositWithNewCard({
  setCompletedDepositAmount,
}: {
  setCompletedDepositAmount?: (amount: number, response?: IPostWalletsDepositsResponse) => void;
}) {
  const { t } = useTranslation('payments');
  const queryClient = useQueryClient();
  const { refetch: refetchWalletBalance } = useWalletBalance();
  const { refetch: refetchRefundableBreakdown } = useWalletRefundableBreakdown();
  const { refetch: refetchPaymentMethods } = usePaymentMethods({
    transactionType: TransactionType.DEPOSIT,
    paymentType: PaymentType.CARD,
  });

  return useMutation({
    mutationFn: async ({
      paysafeInstance,
      depositAmountWithFees,
      merchantRefNum,
      cardholderName,
      billingDetails,
      email,
      bonusId,
    }: {
      paysafeInstance: PaysafeInstance;
      depositAmount: number;
      depositAmountWithFees: number;
      merchantRefNum: string;
      cardholderName: string;
      billingDetails: {
        street: string;
        street2?: string;
        city: string;
        state: string;
        country: string;
        zip: string;
      };
      email: string;
      bonusId?: string;
    }) => {
      const { firstName, lastName } = SplitFullNameToFirstLast(cardholderName);
      const { token: paymentHandleToken } = await paysafeInstance.tokenize({
        amount: dollarsToCents(depositAmountWithFees), // Paysafe requires amount in cents (integer)
        merchantRefNum,
        transactionType: 'PAYMENT',
        paymentType: PaymentType.CARD,
        openAs: 'NEW_TAB',
        customerDetails: {
          holderName: cardholderName,
          billingDetails,
          profile: {
            firstName,
            lastName,
            email,
          },
        },
      });
      return depositToWallet({
        amount: roundToCents(depositAmountWithFees),
        merchantRefNum,
        paymentHandleToken,
        currency: 'USD',
        savePaymentMethod: true,
        ...(bonusId ? { bonusId } : {}),
      });
    },
    onSuccess: (response, payload) => {
      if (response.data.status === 'COMPLETED') {
        reportEvent('Transactions > Deposit', {
          deposit_id: response.id,
          deposit_method: 'Card (new)',
          deposit_amount: payload.depositAmount,
          is_custom_deposit: isCustomDeposit(payload.depositAmount),
          currency: 'USD',
          bonus_id: payload.bonusId,
        });
        refetchWalletBalance();
        refetchRefundableBreakdown();
        refetchPaymentMethods();
        invalidateDepositBonuses(queryClient, payload.bonusId);
        setCompletedDepositAmount?.(payload.depositAmount, response);
      } else {
        bugSnagLog({
          // @ts-expect-error Missing types for error response in Swagger
          errorMessage: `Card deposit not completed: ${response.error}`,
          context: DEPOSIT_CONTEXT,
          severity: 'warning',
          details: { merchantRefNum: payload.merchantRefNum },
          errorDetails: {
            status: response.data.status,
            // @ts-expect-error Missing types for error response in Swagger
            error: response.error,
            // @ts-expect-error Missing types for error response in Swagger
            message: response?.data?.message || response.message,
          },
        });
        showNotification({
          // @ts-expect-error Missing types for error response in Swagger
          title: response.error,
          // @ts-expect-error Missing types for error response in Swagger
          message: response?.data?.message || response.message,
          color: 'red',
          autoClose: 5000,
        });
      }
    },
    onError: (error, { merchantRefNum }) => {
      bugSnagLog({
        ...toBugSnagError(error),
        context: DEPOSIT_CONTEXT,
        details: { merchantRefNum },
      });

      // Paysafe error
      if (isPaysafeError(error)) {
        if ([PaysafeErrorCodes.E5281, PaysafeErrorCodes.E9125].includes(error.code)) {
          showNotification({
            title: t('deposits.errors.paysafe.cardTypeNotSupportedTitle'),
            message: t('deposits.errors.paysafe.cardTypeNotSupportedMessage'),
            color: 'red',
            autoClose: 5000,
          });
          return;
        }

        if (error.code === PaysafeErrorCodes.E3007) {
          showNotification({
            title: t('deposits.errors.paysafe.incorrectAddressTitle'),
            message: t('deposits.errors.paysafe.incorrectAddressMessage'),
            color: 'red',
            autoClose: 5000,
          });
          return;
        }
        showNotification({
          title: t('deposits.errors.paysafe.generalTitle'),
          message: t('deposits.errors.paysafe.generalMessage'),
          color: 'red',
          autoClose: 5000,
        });
        return;
      }

      // Axios error
      if (error instanceof AxiosError) {
        const errorMessage = getErrorTranslations(t, error.code);

        if (error.code === 'ERR_BAD_REQUEST') {
          errorMessage.title = t('deposits.errors.newCard');
          errorMessage.message = error.response?.data?.message ?? '';
        }

        showNotification({
          title: errorMessage.title,
          message: errorMessage.message,
          color: 'red',
          autoClose: 5000,
        });
      }
    },
  });
}

export { useDepositWithNewCard };
